import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { TreasuryInventorySheetService } from "services/treasury-inventory-sheet";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createDenominationRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  denomination: "",
  quantity: "",
  amount: 0,
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const formatQtyInput = (value) => String(value || "").replace(/\D/g, "");
const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number === 0) return "";
  return `${Math.round(number)}`;
};

const calcAmount = (denomination, quantity) => {
  const denom = parseVndIntegerInput(denomination || "") || 0;
  const qty = Number(String(quantity || "").replace(/\D/g, "")) || 0;
  return denom * qty;
};

const isValidDenomination = (value) => (parseVndIntegerInput(value || "") || 0) > 0;
const isValidQuantity = (value) => Number(String(value || "").replace(/\D/g, "")) > 0;
const formatMoneyValue = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

export default function TreasuryInventorySheetDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [inventoryDateParts, setInventoryDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [form, setForm] = useState({
    unit: "",
    address: "",
    documentNumber: "",
    inventoryHour: String(today.getHours()).padStart(2, "0"),
    accountantRepresentative: "",
    cashierRepresentative: "",
    otherRepresentativeName: "",
    otherRepresentativeRole: "",
    reasonExcess: "",
    reasonShortage: "",
    conclusion: "",
  });

  const [openingAmount, setOpeningAmount] = useState("");
  const [denominationRows, setDenominationRows] = useState([createDenominationRow()]);
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await TreasuryInventorySheetService.getById(id);
        const data = response.data ? response.data : response;

        setForm({
          unit: data.unit || "",
          address: data.address || "",
          documentNumber: data.documentNumber || "",
          inventoryHour: data.inventoryHour || "",
          accountantRepresentative: data.accountantRepresentative || "",
          cashierRepresentative: data.cashierRepresentative || "",
          otherRepresentativeName: data.otherRepresentativeName || "",
          otherRepresentativeRole: data.otherRepresentativeRole || "",
          reasonExcess: data.reasonExcess || "",
          reasonShortage: data.reasonShortage || "",
          conclusion: data.conclusion || "",
        });

        setInventoryDateParts(splitDate(data.inventoryDate || ""));

        const rows = Array.isArray(data.rows) ? data.rows : [];
        const openingRow = rows.find((row) => String(row.code) === "I");
        const denominationData = rows.filter((row) => /^\d+$/.test(String(row.code)));

        setOpeningAmount(formatVndIntegerInput(openingRow?.amount || ""));

        if (denominationData.length) {
          const sorted = [...denominationData].sort((a, b) => Number(a.code) - Number(b.code));
          setDenominationRows(
            sorted.map((row) => ({
              id: `${Date.now()}-${Math.random()}`,
              denomination: formatVndIntegerInput(row.denomination || row.amount || ""),
              quantity: formatQtyOutput(row.quantity),
              amount: Number(row.amount || 0),
            }))
          );
        }
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kiểm kê quỹ tiền mặt");
      }
    })();
  }, [id]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setInventoryDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setInventoryDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowError = (id, field, message) => {
    setRowErrors((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: message },
    }));
  };

  const clearRowError = (id, field) => {
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], [field]: "" } };
    });
  };

  const updateDenominationRow = (id, field, value) => {
    setDenominationRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row };
        if (field === "denomination") {
          next.denomination = formatVndIntegerInput(value);
        } else if (field === "quantity") {
          next.quantity = formatQtyInput(value);
        }
        next.amount = calcAmount(next.denomination, next.quantity);
        return next;
      })
    );

    if (field === "denomination") {
      if (!isValidDenomination(value)) {
        setRowError(id, "denomination", "Mệnh giá không hợp lệ");
      } else {
        clearRowError(id, "denomination");
      }
    }

    if (field === "quantity") {
      if (!isValidQuantity(value)) {
        setRowError(id, "quantity", "Số lượng không hợp lệ");
      } else {
        clearRowError(id, "quantity");
      }
    }
  };

  const addDenominationRow = () => {
    setDenominationRows((prev) => [...prev, createDenominationRow()]);
  };

  const removeDenominationRow = () => {
    setDenominationRows((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const openingAmountNumber = parseVndIntegerInput(openingAmount || "") || 0;

  const actualTotal = useMemo(
    () => denominationRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [denominationRows]
  );

  const differenceAmount = openingAmountNumber - actualTotal;

  const buildPayload = () => {
    const payload = {
      unit: String(form.unit || "").trim(),
      address: String(form.address || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      inventoryHour: String(form.inventoryHour || "").trim(),
      inventoryDate: validateDate(inventoryDateParts, "Ngày kiểm kê"),
      accountantRepresentative: String(form.accountantRepresentative || "").trim(),
      cashierRepresentative: String(form.cashierRepresentative || "").trim(),
      otherRepresentativeName: String(form.otherRepresentativeName || "").trim(),
      otherRepresentativeRole: String(form.otherRepresentativeRole || "").trim(),
      reasonExcess: String(form.reasonExcess || "").trim(),
      reasonShortage: String(form.reasonShortage || "").trim(),
      conclusion: String(form.conclusion || "").trim(),
    };

    const required = [
      ["unit", "Đơn vị"],
      ["address", "Địa chỉ"],
      ["documentNumber", "Số"],
      ["inventoryHour", "Giờ kiểm kê"],
      ["accountantRepresentative", "Đại diện kế toán"],
      ["cashierRepresentative", "Đại diện thủ quỹ"],
      ["otherRepresentativeName", "Đại diện"],
      ["otherRepresentativeRole", "Chức danh đại diện"],
    ];

    required.forEach(([key, label]) => {
      if (!payload[key]) throw new Error(`${label} không được để trống`);
    });

    const hourNumber = Number(payload.inventoryHour);
    if (!Number.isFinite(hourNumber) || hourNumber < 0 || hourNumber > 23) {
      throw new Error("Giờ kiểm kê không hợp lệ");
    }

    if (!isValidDenomination(denominationRows[0]?.denomination)) {
      throw new Error("Vui lòng nhập mệnh giá hợp lệ cho dòng loại đầu tiên");
    }

    const rows = denominationRows.map((row, index) => {
      const denominationValue = parseVndIntegerInput(row.denomination || "") || 0;
      if (!isValidDenomination(row.denomination)) {
        throw new Error(`Dòng loại ${index + 1}: Mệnh giá không hợp lệ`);
      }
      if (!isValidQuantity(row.quantity)) {
        throw new Error(`Dòng loại ${index + 1}: Số lượng không hợp lệ`);
      }

      const prefix = index === 0 ? "Trong đó: - Loại" : "- Loại";
      const formattedDenom = formatVndIntegerInput(String(denominationValue));
      return {
        code: String(index + 1),
        description: `${prefix} ${formattedDenom}`.trim(),
        denomination: denominationValue,
        quantity: Number(row.quantity || 0),
        amount: denominationValue * Number(row.quantity || 0),
      };
    });

    const fullRows = [
      {
        code: "I",
        description: "Số dư theo sổ quỹ",
        quantity: 0,
        amount: openingAmountNumber,
      },
      {
        code: "II",
        description: "Số kiểm kê thực tế",
        quantity: 0,
        amount: actualTotal,
      },
      ...rows,
      {
        code: "III",
        description: "Chênh lệch (III = I - II)",
        quantity: 0,
        amount: differenceAmount,
      },
    ];

    return {
      ...payload,
      rows: fullRows,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await TreasuryInventorySheetService.create(payload);
        toast.success("Tạo bảng kiểm kê quỹ tiền mặt thành công");
      } else {
        await TreasuryInventorySheetService.update(id, payload);
        toast.success("Cập nhật bảng kiểm kê quỹ tiền mặt thành công");
      }
      navigation("/templates/treasury-inventory-sheet");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const rows = denominationRows.map((row, index) => {
      const denominationValue = parseVndIntegerInput(row.denomination || "") || 0;
      const prefix = index === 0 ? "Trong đó: - Loại" : "- Loại";
      const formattedDenom = formatVndIntegerInput(String(denominationValue));
      return {
        code: String(index + 1),
        description: `${prefix} ${formattedDenom}`.trim(),
        quantity: Number(row.quantity || 0),
        amount: denominationValue * Number(row.quantity || 0),
      };
    });

    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...form,
        inventoryDate: composeDate(inventoryDateParts),
      },
      rows: [
        {
          code: "I",
          description: "Số dư theo sổ quỹ",
          quantity: 0,
          amount: openingAmountNumber,
        },
        {
          code: "II",
          description: "Số kiểm kê thực tế",
          quantity: 0,
          amount: actualTotal,
        },
        ...rows,
        {
          code: "III",
          description: "Chênh lệch (III = I - II)",
          quantity: 0,
          amount: differenceAmount,
        },
      ],
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const tableRows = useMemo(() => {
    const rows = denominationRows.map((row, index) => ({
      type: "denomination",
      code: String(index + 1),
      denomination: row.denomination,
      quantity: row.quantity,
      amount: row.amount,
      isFirst: index === 0,
      id: row.id,
    }));

    return [
      {
        type: "opening",
        code: "I",
        description: "Số dư theo sổ quỹ",
      },
      {
        type: "actual",
        code: "II",
        description: "Số kiểm kê thực tế",
      },
      ...rows,
      {
        type: "difference",
        code: "III",
        description: "Chênh lệch (III = I - II)",
      },
    ];
  }, [denominationRows]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputCenter: {
      textAlign: "center",
    },
    inputRight: {
      textAlign: "right",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 12,
    },
    th: {
      border: "1px solid #000",
      padding: "6px 4px",
      fontWeight: 700,
      textAlign: "center",
      fontSize: "15px",
    },
    td: {
      border: "1px solid #000",
      padding: "4px 6px",
      fontSize: "15px",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 260 }}
                        value={form.unit}
                        disabled={containView}
                        onChange={(e) => setField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 260 }}
                        value={form.address}
                        disabled={containView}
                        onChange={(e) => setField("address", e.target.value)}
                      />
                    </div>
                  </SoftBox>

                  <SoftBox textAlign="right">
                    <div>
                      <b>Số:</b>{" "}
                      <input
                        style={{ ...s.inputDotted, width: 160, ...s.inputCenter }}
                        value={form.documentNumber}
                        disabled={containView}
                        onChange={(e) => setField("documentNumber", e.target.value)}
                      />
                    </div>
                  </SoftBox>
                </SoftBox>

                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <div style={{ fontSize: "20px", fontWeight: 700 }}>BẢNG KIỂM KÊ QUỸ</div>
                  <div style={{ fontStyle: "italic", marginTop: 4 }}>(Dùng cho VNĐ)</div>
                </div>

                <div style={{ marginTop: 12 }}>
                  Hôm nay, vào{" "}
                  <input
                    style={{ ...s.inputDotted, width: 40, ...s.inputCenter }}
                    value={form.inventoryHour}
                    disabled={containView}
                    onChange={(e) => setField("inventoryHour", e.target.value.replace(/\D/g, "").slice(0, 2))}
                  />{" "}
                  giờ{" "}
                  <input
                    style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                    value={inventoryDateParts.day}
                    disabled={containView}
                    onChange={(e) => setDateField("day", e.target.value)}
                    onBlur={() => padDateField("day")}
                  />{" "}
                  ngày{" "}
                  <input
                    style={{ ...s.inputDotted, width: 34, ...s.inputCenter }}
                    value={inventoryDateParts.month}
                    disabled={containView}
                    onChange={(e) => setDateField("month", e.target.value)}
                    onBlur={() => padDateField("month")}
                  />{" "}
                  tháng{" "}
                  <input
                    style={{ ...s.inputDotted, width: 60, ...s.inputCenter }}
                    value={inventoryDateParts.year}
                    disabled={containView}
                    onChange={(e) => setDateField("year", e.target.value)}
                  />{" "}
                  năm <span>.</span>
                </div>

                <div style={{ marginTop: 10 }}>Chúng tôi gồm:</div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 240 }}
                    value={form.accountantRepresentative}
                    disabled={containView}
                    onChange={(e) => setField("accountantRepresentative", e.target.value)}
                  />{" "}
                  đại diện kế toán
                </div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 240 }}
                    value={form.cashierRepresentative}
                    disabled={containView}
                    onChange={(e) => setField("cashierRepresentative", e.target.value)}
                  />{" "}
                  đại diện thủ quỹ
                </div>
                <div style={{ marginTop: 4 }}>
                  - Ông/Bà:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 240 }}
                    value={form.otherRepresentativeName}
                    disabled={containView}
                    onChange={(e) => setField("otherRepresentativeName", e.target.value)}
                  />{" "}
                  đại diện{" "}
                  <input
                    style={{ ...s.inputDotted, width: 200 }}
                    value={form.otherRepresentativeRole}
                    disabled={containView}
                    onChange={(e) => setField("otherRepresentativeRole", e.target.value)}
                  />
                </div>

                <div style={{ marginTop: 10 }}>Cùng tiến hành kiểm kê quỹ tiền mặt kết quả như sau</div>

                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>STT</th>
                      <th style={s.th}>Diễn giải</th>
                      <th style={s.th}>Số lượng(tờ)</th>
                      <th style={s.th}>Số tiền</th>
                    </tr>
                    <tr>
                      <th style={s.th}>A</th>
                      <th style={s.th}>B</th>
                      <th style={s.th}>1</th>
                      <th style={s.th}>2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => {
                      if (row.type === "opening") {
                        return (
                          <tr key={row.code}>
                            <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>{row.code}</td>
                            <td style={s.td}>{row.description}</td>
                            <td style={{ ...s.td, textAlign: "center" }}>x</td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={openingAmount}
                                disabled={containView}
                                onChange={(e) => setOpeningAmount(formatVndIntegerInput(e.target.value))}
                              />
                            </td>
                          </tr>
                        );
                      }

                      if (row.type === "actual") {
                        return (
                          <tr key={row.code}>
                            <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>{row.code}</td>
                            <td style={s.td}>{row.description}</td>
                            <td style={{ ...s.td, textAlign: "center" }}>x</td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={formatMoneyValue(actualTotal)}
                                disabled
                                readOnly
                              />
                            </td>
                          </tr>
                        );
                      }

                      if (row.type === "difference") {
                        return (
                          <tr key={row.code}>
                            <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>{row.code}</td>
                            <td style={s.td}>{row.description}</td>
                            <td style={{ ...s.td, textAlign: "center" }}>x</td>
                            <td style={{ ...s.td, textAlign: "right" }}>
                              <input
                                style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                                value={formatMoneyValue(differenceAmount)}
                                disabled
                                readOnly
                              />
                            </td>
                          </tr>
                        );
                      }

                      const denomError = rowErrors[row.id] || {};

                      return (
                        <tr key={row.id}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>{row.code}</td>
                          <td style={s.td}>
                            {row.isFirst ? "Trong đó: - Loại" : "- Loại"}{" "}
                            <input
                              style={{
                                ...s.inputDotted,
                                width: 160,
                                borderBottomColor: denomError.denomination ? "#d32f2f" : "#000",
                              }}
                              value={row.denomination}
                              disabled={containView}
                              onChange={(e) => updateDenominationRow(row.id, "denomination", e.target.value)}
                              onBlur={() =>
                                !isValidDenomination(row.denomination) && toast.error("Mệnh giá không hợp lệ")
                              }
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "center" }}>
                            <input
                              style={{
                                ...s.inputDotted,
                                width: "100%",
                                ...s.inputRight,
                                borderBottomColor: denomError.quantity ? "#d32f2f" : "#000",
                              }}
                              value={row.quantity}
                              disabled={containView}
                              onChange={(e) => updateDenominationRow(row.id, "quantity", e.target.value)}
                              onBlur={() =>
                                !isValidQuantity(row.quantity) && toast.error("Số lượng không hợp lệ")
                              }
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right" }}>
                            <input
                              style={{ ...s.inputDotted, width: "100%", ...s.inputRight }}
                              value={formatMoneyValue(row.amount || 0)}
                              disabled
                              readOnly
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {!containView && (
                  <SoftBox mt={2} display="flex" gap={2} justifyContent="flex-end">
                    <SoftButton variant="outlined" color="dark" onClick={addDenominationRow}>
                      THÊM DÒNG
                    </SoftButton>
                    <SoftButton variant="outlined" color="error" onClick={removeDenominationRow}>
                      XÓA DÒNG
                    </SoftButton>
                  </SoftBox>
                )}

                <div style={{ marginTop: 10 }}>- Lý do:</div>
                <div style={{ marginTop: 4, paddingLeft: 14 }}>
                  + Thừa:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 260 }}
                    value={form.reasonExcess}
                    disabled={containView}
                    onChange={(e) => setField("reasonExcess", e.target.value)}
                  />
                </div>
                <div style={{ marginTop: 4, paddingLeft: 14 }}>
                  + Thiếu:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 260 }}
                    value={form.reasonShortage}
                    disabled={containView}
                    onChange={(e) => setField("reasonShortage", e.target.value)}
                  />
                </div>
                <div style={{ marginTop: 6 }}>
                  - Kết luận sau khi kiểm kê quỹ:{" "}
                  <input
                    style={{ ...s.inputDotted, width: 400 }}
                    value={form.conclusion}
                    disabled={containView}
                    onChange={(e) => setField("conclusion", e.target.value)}
                  />
                </div>

                <SoftBox mt={4} display="flex" justifyContent="space-between" alignItems="center">
                  <SoftButton variant="outlined" color="info" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
